// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { HeaderSize } from "@tps/Overrides/Pages/Header";
import IconTextLinkArrowLeftInactive from "@tps/XDL/Icons/IconTextLinkArrowLeftInactive";
import { iconLinkCommonStyles } from "@tps/XDL/Utilities";

import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    signInOverlay?: ReactNode;
    showSignInOverlay?: boolean;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

const mapStateToProps = (state: CustomAppState) => {
    const pageType: any = getCurrentPage(state).type;

    return {
        isPdpPage: pageType === "ProductDetailsPage",
        isCheckoutPages: pageType === "CheckoutShippingPage" || pageType === "CheckoutReviewAndSubmitPage",
        isOrderConfirmationPage: pageType === "OrderConfirmationPage",
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        notificationBarFromState: state.xHeader.notificationBar,
    };
};

export interface PageLayoutStyles {
    page?: InjectableCss;
    returnToCartLink?: LinkPresentationProps;
}

export const styles: PageLayoutStyles = {
    page: {
        // breadcrumb page component
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 0;
            margin-top: 12px;
            @media (max-width: 768px) {
                position: static !important;
            }
            @media print {
                display: none !important;
            }
        `,
    },
    returnToCartLink: {
        ...iconLinkCommonStyles(IconTextLinkArrowLeftInactive),
    },
};

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps> & HasHistory;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    isCheckoutPages,
    isOrderConfirmationPage,
    history,
    cartPageUrl,
    notificationBarFromState,
    signInOverlay,
    showSignInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: Props) => {
    const navigateToCartPage = () => {
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };

    const [notificationBar, setNotificationBar] = React.useState(true);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const closedState = sessionStorage?.getItem("closed");
        setNotificationBar(!(closedState && closedState === "true"));
    }, [notificationBarFromState]);

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                @media print {
                    display: block !important;
                }
            `}
        >
            {showHeader && header}

            <StyledWrapper
                css={css`
                    background-color: ${getColor("common.white")};
                    min-height: inherit;
                    flex-grow: 1;

                    @media (max-width: 1024px) {
                        padding-top: ${showHeader
                            ? isCheckoutPages || isOrderConfirmationPage
                                ? HeaderSize.checkoutMobileHeader
                                : !notificationBar
                                ? HeaderSize.mobile - HeaderSize.notificationBar
                                : HeaderSize.mobile
                            : HeaderSize.mobile}px;
                    }

                    @media print {
                        padding-top: 0;
                    }
                `}
                className="main-page"
            >
                {showBreadcrumbs && !(isCheckoutPages || isOrderConfirmationPage) && (
                    <Page as="div" {...styles.page}>
                        <PageBreadcrumbs />
                    </Page>
                )}
                {isCheckoutPages && (
                    <Page
                        as="div"
                        css={css`
                            ${styles.page?.css}
                            margin-bottom: 16px;
                        `}
                    >
                        <Link {...styles.returnToCartLink} onClick={navigateToCartPage}>
                            {translate("Back to Cart")}
                        </Link>
                    </Page>
                )}
                {pageContent}
            </StyledWrapper>

            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(PageLayout));
